import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ttApi } from "../../lib/ttApi";
import {
  ArrowLeft, Plus, Trash2, Loader2, AlertTriangle, Clock, Shield, HeartPulse, Megaphone, Sparkles, Zap,
} from "lucide-react";

const Tile = ({ label, value, sub, tone }) => {
  const toneClass = { green: "text-emerald-700", amber: "text-amber-700", red: "text-red-700" }[tone] || "text-zinc-900";
  return (
    <div className="bg-white border border-zinc-200 rounded-md p-4">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-3xl font-extrabold tracking-tight mt-1 ${toneClass}`}>{value}</div>
      <div className="text-xs text-zinc-500 mt-1">{sub}</div>
    </div>
  );
};
const L = ({ label, children }) => (<label className="block"><div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{label}</div>{children}</label>);
const F = ({ label, v, onChange, testid, type = "text" }) => (<L label={label}><input type={type} value={v || ""} onChange={e => onChange(e.target.value)} className="fld py-2 px-3 w-full" data-testid={testid}/></L>);
const Err = ({ children }) => <div className="p-2 bg-red-50 border-l-4 border-red-500 text-sm text-red-800">{children}</div>;
const SmallModal = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-[80] grid place-items-center px-4" style={{ background: "rgba(15,23,42,0.55)" }} onClick={onClose} data-testid={testid}>
    <div className="bg-white rounded-md shadow-2xl max-w-md w-full p-5 max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
      <div className="font-display text-lg font-extrabold tracking-tight mb-3">{title}</div>
      {children}
    </div>
  </div>
);
const Actions = ({ onClose, busy, testid, label = "Save" }) => (
  <div className="flex items-center justify-end gap-2 pt-2">
    <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">Cancel</button>
    <button type="submit" disabled={busy} className="btn btn-primary btn-sm" data-testid={testid}>{busy ? "Saving…" : label}</button>
  </div>
);
const BackLink = () => <Link to="/test-tech/home" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"><ArrowLeft size={13}/> Control Tower</Link>;

// ─── Readiness Center ────────────────────────────────────────────────────
const CHECK_FIELDS = ["availability","application_version","configuration","interfaces","user_accounts","security_roles","test_data","batch_jobs","middleware","network","certificates","sso"];

export function ReadinessCenter() {
  const { pid } = useParams();
  const [envs, setEnvs] = useState([]);
  const [open, setOpen] = useState(null); // { env } or null for new
  const [busy, setBusy] = useState(true);
  const refresh = async () => { setBusy(true); try { const r = await ttApi.listEnvs(pid); setEnvs(r.items || []); } finally { setBusy(false); } };
  useEffect(() => { refresh(); }, [pid]);
  const del = async (eid) => { if (window.confirm("Delete environment?")) { await ttApi.deleteEnv(pid, eid); refresh(); } };
  const avg = envs.length ? Math.round(envs.reduce((a, e) => a + (e.readiness_pct || 0), 0) / envs.length) : 0;
  const reds = envs.filter(e => e.rag === "red").length;
  const greens = envs.filter(e => e.rag === "green").length;
  return (
    <main className="max-w-6xl mx-auto px-6 py-8" data-testid="tt-readiness">
      <BackLink/>
      <div className="flex items-center justify-between mt-2 mb-5">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Readiness Command Center</h1>
        <button onClick={() => setOpen({})} className="btn btn-primary btn-sm inline-flex items-center gap-1" data-testid="tt-new-env"><Plus size={13}/> New environment</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Tile label="Environments" value={envs.length} sub="total"/>
        <Tile label="Avg Readiness" value={`${avg}%`} sub="across envs" tone={avg >= 90 ? "green" : avg >= 70 ? "amber" : "red"}/>
        <Tile label="Green" value={greens} sub="≥ 90%" tone="green"/>
        <Tile label="Red" value={reds} sub="< 70%" tone={reds > 0 ? "red" : "green"}/>
      </div>
      {busy ? <div className="text-sm text-zinc-500"><Loader2 className="inline animate-spin" size={13}/> Loading…</div> : (
        <div className="space-y-3">
          {envs.length === 0 && <div className="p-6 bg-white border border-dashed rounded-sm text-center text-sm text-zinc-500">No environments yet. Click <b>New environment</b> to begin.</div>}
          {envs.map(e => (
            <div key={e._id} className="bg-white border border-zinc-200 rounded-md p-4" data-testid={`tt-env-${e._id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{e.stage}</div>
                  <div className="font-display text-lg font-extrabold">{e.name}</div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className={`font-display text-2xl font-extrabold ${e.rag === "green" ? "text-emerald-700" : e.rag === "amber" ? "text-amber-700" : "text-red-700"}`}>{e.readiness_pct}%</div>
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{e.rag.toUpperCase()}</div>
                  </div>
                  <button onClick={() => setOpen({ env: e })} className="text-xs text-zinc-700 hover:text-zinc-900 underline" data-testid={`tt-env-edit-${e._id}`}>Edit</button>
                  <button onClick={() => del(e._id)} className="text-xs text-red-700"><Trash2 size={12}/></button>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                {CHECK_FIELDS.map(f => {
                  const v = (e.checks || {})[f] || "n/a";
                  const cls = v === "green" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : v === "amber" ? "bg-amber-50 text-amber-800 border-amber-200"
                            : v === "red" ? "bg-red-50 text-red-700 border-red-200"
                            : "bg-zinc-50 text-zinc-500 border-zinc-200";
                  return <div key={f} className={`text-[11px] font-mono px-2 py-1 border rounded-sm ${cls}`}>{f.replace(/_/g," ")}: {v}</div>;
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      {open !== null && <EnvModal pid={pid} initial={open.env} onClose={() => setOpen(null)} onDone={() => { setOpen(null); refresh(); }}/>}
    </main>
  );
}

function EnvModal({ pid, initial, onClose, onDone }) {
  const [f, setF] = useState({
    name: initial?.name || "", stage: initial?.stage || "SIT",
    checks: initial?.checks || Object.fromEntries(CHECK_FIELDS.map(k => [k, "n/a"])),
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (initial?._id) await ttApi.updateEnv(pid, initial._id, f);
      else await ttApi.createEnv(pid, f);
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <SmallModal title={initial?._id ? "Edit Environment" : "New Environment"} onClose={onClose} testid="tt-env-modal">
      <form onSubmit={submit} className="space-y-3">
        <F label="Name" v={f.name} onChange={v => setF({...f, name: v})} testid="tt-env-name"/>
        <L label="Stage">
          <select value={f.stage} onChange={e => setF({...f, stage: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-env-stage">
            {["DEV","TEST","QA","SIT","UAT","PRE-PROD","PROD"].map(x => <option key={x}>{x}</option>)}
          </select>
        </L>
        <div className="border-t pt-3 space-y-1">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Readiness checks</div>
          {CHECK_FIELDS.map(k => (
            <div key={k} className="flex items-center justify-between text-sm">
              <span className="font-mono text-xs text-zinc-700">{k.replace(/_/g," ")}</span>
              <select value={f.checks[k] || "n/a"} onChange={e => setF({...f, checks: {...f.checks, [k]: e.target.value}})} className="fld py-1 px-2 text-xs" data-testid={`tt-env-check-${k}`}>
                {["n/a","red","amber","green"].map(x => <option key={x}>{x}</option>)}
              </select>
            </div>
          ))}
        </div>
        {err && <Err>{err}</Err>}
        <Actions onClose={onClose} busy={busy} testid="tt-env-submit"/>
      </form>
    </SmallModal>
  );
}

// ─── Cutover Center ──────────────────────────────────────────────────────
export function CutoverCenter() {
  const { pid } = useParams();
  const [data, setData] = useState({ items: [], totals: {} });
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(true);
  const refresh = async () => { setBusy(true); try { const r = await ttApi.listCutover(pid); setData(r); } finally { setBusy(false); } };
  useEffect(() => { refresh(); }, [pid]);
  const del = async (tid) => { if (window.confirm("Delete task?")) { await ttApi.deleteCut(pid, tid); refresh(); } };
  const T = data.totals || {};
  return (
    <main className="max-w-6xl mx-auto px-6 py-8" data-testid="tt-cutover">
      <BackLink/>
      <div className="flex items-center justify-between mt-2 mb-5">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Cutover Command Center</h1>
        <button onClick={() => setOpen({})} className="btn btn-primary btn-sm inline-flex items-center gap-1" data-testid="tt-new-cutover"><Plus size={13}/> New task</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Tile label="Tasks" value={T.tasks || 0} sub={`${T.completed || 0} done`}/>
        <Tile label="Complete %" value={`${T.pct_complete || 0}%`} sub="of plan" tone={(T.pct_complete || 0) >= 90 ? "green" : (T.pct_complete || 0) >= 50 ? "amber" : "red"}/>
        <Tile label="Blocked" value={T.blocked || 0} sub="in-flight" tone={T.blocked > 0 ? "red" : "green"}/>
        <Tile label="Overrun" value={T.overrun || 0} sub="actual > planned" tone={T.overrun > 0 ? "amber" : "green"}/>
      </div>
      {busy ? <div className="text-sm text-zinc-500"><Loader2 className="inline animate-spin" size={13}/> Loading…</div> : (
        <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-widest text-zinc-500">
              <tr><th className="text-left px-3 py-2">#</th><th className="text-left px-3 py-2">Task</th><th className="text-left px-3 py-2">Owner</th><th className="text-left px-3 py-2">Planned</th><th className="text-left px-3 py-2">Actual</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">CP</th><th className="text-right px-3 py-2">Actions</th></tr>
            </thead>
            <tbody>
              {(data.items || []).length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-zinc-500">No cutover tasks yet.</td></tr>}
              {(data.items || []).map(t => (
                <tr key={t._id} className="border-t border-zinc-100" data-testid={`tt-cutover-${t._id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{t.seq}</td>
                  <td className="px-3 py-2">{t.task}{t.rollback_step && <div className="text-[10px] font-mono text-zinc-400 mt-0.5">↩ rollback: {t.rollback_step}</div>}</td>
                  <td className="px-3 py-2 text-xs text-zinc-500">{t.owner || "—"}</td>
                  <td className="px-3 py-2 text-xs text-zinc-500 font-mono">{t.planned_start?.slice(0,16).replace("T"," ") || "—"} → {t.planned_end?.slice(0,16).replace("T"," ") || "—"}</td>
                  <td className="px-3 py-2 text-xs text-zinc-500 font-mono">{t.actual_start?.slice(0,16).replace("T"," ") || "—"} → {t.actual_end?.slice(0,16).replace("T"," ") || "—"}</td>
                  <td className="px-3 py-2"><span className="pill pill-neutral text-[10px]">{t.status}</span></td>
                  <td className="px-3 py-2">{t.on_critical_path && <span className="pill text-[9px] bg-red-50 text-red-700 border border-red-200 inline-flex items-center gap-1"><Zap size={10}/> CP</span>}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setOpen({ task: t })} className="text-xs text-zinc-700 hover:text-zinc-900 underline" data-testid={`tt-cutover-edit-${t._id}`}>Edit</button>
                    <button onClick={() => del(t._id)} className="ml-3 text-xs text-red-700"><Trash2 size={11}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open !== null && <CutModal pid={pid} initial={open.task} onClose={() => setOpen(null)} onDone={() => { setOpen(null); refresh(); }}/>}
    </main>
  );
}

function CutModal({ pid, initial, onClose, onDone }) {
  const [f, setF] = useState({
    seq: initial?.seq ?? 0, task: initial?.task || "", owner: initial?.owner || "",
    planned_start: initial?.planned_start || "", planned_end: initial?.planned_end || "",
    actual_start: initial?.actual_start || "", actual_end: initial?.actual_end || "",
    duration_min: initial?.duration_min ?? 0, status: initial?.status || "Not Started",
    rollback_step: initial?.rollback_step || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const body = { ...f, seq: parseInt(f.seq) || 0, duration_min: parseInt(f.duration_min) || 0 };
      if (initial?._id) await ttApi.updateCut(pid, initial._id, body);
      else await ttApi.createCut(pid, body);
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <SmallModal title={initial?._id ? "Edit Task" : "New Cutover Task"} onClose={onClose} testid="tt-cutover-modal">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <F label="Seq #" v={f.seq} onChange={v => setF({...f, seq: v})} testid="tt-cut-seq" type="number"/>
          <F label="Duration (min)" v={f.duration_min} onChange={v => setF({...f, duration_min: v})} testid="tt-cut-dur" type="number"/>
        </div>
        <F label="Task" v={f.task} onChange={v => setF({...f, task: v})} testid="tt-cut-task"/>
        <F label="Owner" v={f.owner} onChange={v => setF({...f, owner: v})} testid="tt-cut-owner"/>
        <div className="grid grid-cols-2 gap-3">
          <F label="Planned start" v={f.planned_start} onChange={v => setF({...f, planned_start: v})} testid="tt-cut-ps" type="datetime-local"/>
          <F label="Planned end" v={f.planned_end} onChange={v => setF({...f, planned_end: v})} testid="tt-cut-pe" type="datetime-local"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Actual start" v={f.actual_start} onChange={v => setF({...f, actual_start: v})} testid="tt-cut-as" type="datetime-local"/>
          <F label="Actual end" v={f.actual_end} onChange={v => setF({...f, actual_end: v})} testid="tt-cut-ae" type="datetime-local"/>
        </div>
        <L label="Status">
          <select value={f.status} onChange={e => setF({...f, status: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-cut-status">
            {["Not Started","In Progress","Blocked","Completed","Skipped"].map(x => <option key={x}>{x}</option>)}
          </select>
        </L>
        <F label="Rollback step (if things go wrong)" v={f.rollback_step} onChange={v => setF({...f, rollback_step: v})} testid="tt-cut-rb"/>
        {err && <Err>{err}</Err>}
        <Actions onClose={onClose} busy={busy} testid="tt-cut-submit"/>
      </form>
    </SmallModal>
  );
}

// ─── Hypercare + War Room ────────────────────────────────────────────────
const INC_STATUSES = ["Reported","Validated","Triaged","Assigned","Investigating","Fix Identified","Fix Applied","Business Validation","Closed"];

export function HypercareCenter() {
  const { pid } = useParams();
  const [data, setData] = useState({ items: [], totals: {} });
  const [open, setOpen] = useState(null);
  const [war, setWar] = useState(null);
  const [warBusy, setWarBusy] = useState(false);
  const refresh = async () => { const r = await ttApi.listIncs(pid); setData(r); };
  useEffect(() => { refresh(); }, [pid]);
  const del = async (iid) => { if (window.confirm("Delete incident?")) { await ttApi.deleteInc(pid, iid); refresh(); } };
  const generateWar = async () => { setWarBusy(true); try { setWar(await ttApi.warRoom(pid)); } finally { setWarBusy(false); } };
  const T = data.totals || { by_priority: {} };
  const bp = T.by_priority || {};
  return (
    <main className="max-w-6xl mx-auto px-6 py-8" data-testid="tt-hypercare">
      <BackLink/>
      <div className="flex items-center justify-between mt-2 mb-5">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Hypercare Command Center</h1>
        <div className="flex gap-2">
          <button onClick={generateWar} disabled={warBusy} className="btn btn-secondary btn-sm inline-flex items-center gap-1" data-testid="tt-war-room">
            {warBusy ? <Loader2 className="animate-spin" size={13}/> : <Megaphone size={13}/>} War-Room Briefing
          </button>
          <button onClick={() => setOpen({})} className="btn btn-primary btn-sm inline-flex items-center gap-1" data-testid="tt-new-inc"><Plus size={13}/> New incident</button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5" data-testid="tt-hc-kpis">
        <Tile label="All" value={T.all || 0} sub={`${T.open || 0} open`}/>
        <Tile label="P1" value={bp.P1 || 0} sub="critical" tone={bp.P1 > 0 ? "red" : "green"}/>
        <Tile label="P2" value={bp.P2 || 0} sub="high" tone={bp.P2 > 0 ? "amber" : "green"}/>
        <Tile label="SLA breached" value={T.sla_breached || 0} sub="open & past target" tone={T.sla_breached > 0 ? "red" : "green"}/>
        <Tile label="P3+P4" value={(bp.P3 || 0) + (bp.P4 || 0)} sub="medium / low"/>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-widest text-zinc-500">
            <tr><th className="text-left px-3 py-2">ID</th><th className="text-left px-3 py-2">Title</th><th className="text-left px-3 py-2">Priority</th><th className="text-left px-3 py-2">Tier</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">Owner</th><th className="text-left px-3 py-2">SLA</th><th className="text-right px-3 py-2">Actions</th></tr>
          </thead>
          <tbody>
            {(data.items || []).length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-sm text-zinc-500">No incidents logged.</td></tr>}
            {(data.items || []).map(i => (
              <tr key={i._id} className="border-t border-zinc-100" data-testid={`tt-inc-${i._id}`}>
                <td className="px-3 py-2 font-mono text-xs">{i._id.slice(-6)}</td>
                <td className="px-3 py-2">{i.title}</td>
                <td className="px-3 py-2"><PrioPill p={i.priority}/></td>
                <td className="px-3 py-2 text-xs"><span className="pill pill-neutral text-[10px]">{i.tier || "L1"}</span></td>
                <td className="px-3 py-2 text-xs"><span className="pill pill-neutral text-[10px]">{i.status}</span></td>
                <td className="px-3 py-2 text-xs text-zinc-500">{i.owner || "—"}</td>
                <td className="px-3 py-2 text-xs font-mono">
                  {i.sla?.closed ? <span className="text-emerald-700">closed</span> :
                    i.sla?.breached ? <span className="text-red-700">BREACH · {Math.floor(i.sla.elapsed_min/60)}h</span> :
                    <span className="text-zinc-600">{Math.floor(i.sla.elapsed_min/60)}h / {Math.floor(i.sla.resolution_target_min/60)}h</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setOpen({ inc: i })} className="text-xs text-zinc-700 hover:text-zinc-900 underline" data-testid={`tt-inc-edit-${i._id}`}>Edit</button>
                  <button onClick={() => del(i._id)} className="ml-3 text-xs text-red-700"><Trash2 size={11}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {war && (
        <div className="mt-6 p-5 bg-white border-l-4 border-red-600 rounded-md shadow" data-testid="tt-war-briefing">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-sm grid place-items-center text-white shrink-0" style={{ background: "linear-gradient(135deg,#4A0F1A 0%,#B91C1C 100%)" }}>
              <Megaphone size={14}/>
            </div>
            <div className="flex-1">
              <div className="font-mono text-[10px] uppercase tracking-widest text-red-700">War-Room briefing · generated {new Date(war.generated_at).toLocaleTimeString()}</div>
              <h2 className="font-display text-xl font-extrabold tracking-tight">{war.program}</h2>
              {war.ai_briefing && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-sm">
                  <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-red-700 mb-1 inline-flex items-center gap-1"><Sparkles size={10}/> NAVBOT</div>
                  <div className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{war.ai_briefing}</div>
                </div>
              )}
              <div className="mt-3 text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{war.template_briefing}</div>
            </div>
          </div>
        </div>
      )}

      {open !== null && <IncModal pid={pid} initial={open.inc} onClose={() => setOpen(null)} onDone={() => { setOpen(null); refresh(); }}/>}
    </main>
  );
}

function PrioPill({ p }) {
  const map = { P1: "bg-red-50 text-red-700 border border-red-200", P2: "bg-amber-50 text-amber-800 border border-amber-200", P3: "pill-neutral", P4: "pill-neutral" };
  return <span className={`pill text-[10px] ${map[p] || "pill-neutral"}`}>{p}</span>;
}

function IncModal({ pid, initial, onClose, onDone }) {
  const [f, setF] = useState({
    title: initial?.title || "", reporter: initial?.reporter || "",
    tier: initial?.tier || "L1", priority: initial?.priority || "P3",
    business_impact: initial?.business_impact || "", users_impacted: initial?.users_impacted || 0,
    revenue_impact: initial?.revenue_impact || 0, process: initial?.process || "",
    application: initial?.application || "", workaround: initial?.workaround || "",
    status: initial?.status || "Reported", owner: initial?.owner || "",
    description: initial?.description || "", root_cause: initial?.root_cause || "", resolution: initial?.resolution || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const body = { ...f, users_impacted: parseInt(f.users_impacted) || 0, revenue_impact: parseFloat(f.revenue_impact) || 0 };
      if (initial?._id) await ttApi.updateInc(pid, initial._id, body);
      else await ttApi.createInc(pid, body);
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <SmallModal title={initial?._id ? "Edit Incident" : "New Incident"} onClose={onClose} testid="tt-inc-modal">
      <form onSubmit={submit} className="space-y-3">
        <F label="Title" v={f.title} onChange={v => setF({...f, title: v})} testid="tt-inc-title"/>
        <div className="grid grid-cols-3 gap-3">
          <L label="Priority"><select value={f.priority} onChange={e => setF({...f, priority: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-inc-priority">{["P1","P2","P3","P4"].map(x => <option key={x}>{x}</option>)}</select></L>
          <L label="Tier"><select value={f.tier} onChange={e => setF({...f, tier: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-inc-tier">{["L1","L2","L3","L4"].map(x => <option key={x}>{x}</option>)}</select></L>
          <L label="Status"><select value={f.status} onChange={e => setF({...f, status: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-inc-status">{INC_STATUSES.map(x => <option key={x}>{x}</option>)}</select></L>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Owner" v={f.owner} onChange={v => setF({...f, owner: v})} testid="tt-inc-owner"/>
          <F label="Reporter" v={f.reporter} onChange={v => setF({...f, reporter: v})} testid="tt-inc-reporter"/>
        </div>
        <F label="Business impact" v={f.business_impact} onChange={v => setF({...f, business_impact: v})} testid="tt-inc-impact"/>
        <div className="grid grid-cols-2 gap-3">
          <F label="Users impacted" v={f.users_impacted} onChange={v => setF({...f, users_impacted: v})} testid="tt-inc-users" type="number"/>
          <F label="Revenue impact ($)" v={f.revenue_impact} onChange={v => setF({...f, revenue_impact: v})} testid="tt-inc-rev" type="number"/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <F label="Process" v={f.process} onChange={v => setF({...f, process: v})} testid="tt-inc-process"/>
          <F label="Application" v={f.application} onChange={v => setF({...f, application: v})} testid="tt-inc-app"/>
        </div>
        <F label="Workaround" v={f.workaround} onChange={v => setF({...f, workaround: v})} testid="tt-inc-wa"/>
        <F label="Description" v={f.description} onChange={v => setF({...f, description: v})} testid="tt-inc-desc"/>
        <F label="Root cause" v={f.root_cause} onChange={v => setF({...f, root_cause: v})} testid="tt-inc-rc"/>
        <F label="Resolution" v={f.resolution} onChange={v => setF({...f, resolution: v})} testid="tt-inc-res"/>
        {err && <Err>{err}</Err>}
        <Actions onClose={onClose} busy={busy} testid="tt-inc-submit"/>
      </form>
    </SmallModal>
  );
}

// ─── Requirements (RTM) ──────────────────────────────────────────────────
export function RequirementsCenter() {
  const { pid } = useParams();
  const [rtm, setRtm] = useState({ items: [], uncovered: [], coverage_pct: 0, totals: {} });
  const [open, setOpen] = useState(null);
  const refresh = async () => setRtm(await ttApi.rtm(pid));
  useEffect(() => { refresh(); }, [pid]);
  const del = async (rid) => { if (window.confirm("Delete requirement?")) { await ttApi.deleteReq(pid, rid); refresh(); } };
  const T = rtm.totals || {};
  return (
    <main className="max-w-6xl mx-auto px-6 py-8" data-testid="tt-requirements">
      <BackLink/>
      <div className="flex items-center justify-between mt-2 mb-5">
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Requirements Traceability (RTM)</h1>
        <button onClick={() => setOpen({})} className="btn btn-primary btn-sm inline-flex items-center gap-1" data-testid="tt-new-req"><Plus size={13}/> New requirement</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Tile label="Requirements" value={T.requirements || 0} sub="total"/>
        <Tile label="Coverage %" value={`${rtm.coverage_pct}%`} sub="have scenarios" tone={rtm.coverage_pct >= 90 ? "green" : rtm.coverage_pct >= 70 ? "amber" : "red"}/>
        <Tile label="Uncovered" value={(rtm.uncovered || []).length} sub="no scenario yet" tone={(rtm.uncovered || []).length > 0 ? "red" : "green"}/>
        <Tile label="Chain nodes" value={(T.scenarios || 0) + (T.cases || 0) + (T.defects || 0)} sub="scenarios+cases+defects"/>
      </div>
      {rtm.uncovered?.length > 0 && (
        <div className="mb-5 p-3 bg-red-50 border-l-4 border-red-500 text-sm text-red-800" data-testid="tt-uncovered">
          <b>Uncovered requirements:</b> {rtm.uncovered.map(r => r.code).join(", ")}
        </div>
      )}
      <div className="space-y-3">
        {(rtm.items || []).length === 0 && <div className="p-6 bg-white border border-dashed rounded-sm text-center text-sm text-zinc-500">No requirements yet. Click <b>New requirement</b> and start building the traceability chain.</div>}
        {(rtm.items || []).map(({ requirement: r, scenarios }) => (
          <div key={r._id} className="bg-white border border-zinc-200 rounded-md p-4" data-testid={`tt-req-${r._id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-xs text-[#7A1F2B] uppercase tracking-widest">{r.code} · {r.priority}</div>
                <div className="font-display text-base font-extrabold">{r.title}</div>
                <div className="text-xs text-zinc-500 mt-0.5">{r.business_process || "—"}{r.system ? ` · ${r.system}` : ""}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setOpen({ req: r })} className="text-xs text-zinc-700 underline">Edit</button>
                <button onClick={() => del(r._id)} className="text-xs text-red-700"><Trash2 size={11}/></button>
              </div>
            </div>
            {scenarios.length === 0 ? (
              <div className="mt-3 text-xs text-amber-700 bg-amber-50 border-l-2 border-amber-400 px-2 py-1">⚠ No scenario references this requirement. Add <code>{r.code}</code> to a scenario's requirement_ids.</div>
            ) : (
              <div className="mt-3 ml-3 border-l-2 border-zinc-200 pl-3 space-y-2">
                {scenarios.map(({ scenario: s, cases }) => (
                  <div key={s._id}>
                    <div className="text-sm font-semibold">↳ {s.name} <span className="font-mono text-[9px] uppercase text-zinc-400">{s.process || ""} · {s.type}</span></div>
                    {cases.length === 0 ? <div className="text-xs text-zinc-500 ml-4">no cases</div> : (
                      <div className="ml-4 mt-1 space-y-1">
                        {cases.map(({ case: c, defects }) => (
                          <div key={c._id} className="text-xs flex items-center gap-2">
                            <span>↳ {c.title}</span>
                            <span className={`pill text-[9px] ${c.status === "Pass" ? "pill-green" : c.status === "Fail" ? "bg-red-50 text-red-700 border border-red-200" : "pill-neutral"}`}>{c.status || "Not Run"}</span>
                            {defects.length > 0 && <span className="pill text-[9px] bg-red-50 text-red-700 border border-red-200">{defects.length} defect{defects.length !== 1 ? "s" : ""}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {open !== null && <ReqModal pid={pid} initial={open.req} onClose={() => setOpen(null)} onDone={() => { setOpen(null); refresh(); }}/>}
    </main>
  );
}

function ReqModal({ pid, initial, onClose, onDone }) {
  const [f, setF] = useState({
    code: initial?.code || "", title: initial?.title || "",
    business_process: initial?.business_process || "", system: initial?.system || "",
    priority: initial?.priority || "Medium", description: initial?.description || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (initial?._id) await ttApi.updateReq(pid, initial._id, f);
      else await ttApi.createReq(pid, f);
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  return (
    <SmallModal title={initial?._id ? "Edit Requirement" : "New Requirement"} onClose={onClose} testid="tt-req-modal">
      <form onSubmit={submit} className="space-y-3">
        <F label="Code (e.g. OTC-REQ-001245)" v={f.code} onChange={v => setF({...f, code: v})} testid="tt-req-code"/>
        <F label="Title" v={f.title} onChange={v => setF({...f, title: v})} testid="tt-req-title"/>
        <div className="grid grid-cols-2 gap-3">
          <F label="Business process" v={f.business_process} onChange={v => setF({...f, business_process: v})} testid="tt-req-process"/>
          <F label="System" v={f.system} onChange={v => setF({...f, system: v})} testid="tt-req-system"/>
        </div>
        <L label="Priority">
          <select value={f.priority} onChange={e => setF({...f, priority: e.target.value})} className="fld py-2 px-3 w-full" data-testid="tt-req-priority">
            {["Low","Medium","High","Critical"].map(x => <option key={x}>{x}</option>)}
          </select>
        </L>
        <F label="Description" v={f.description} onChange={v => setF({...f, description: v})} testid="tt-req-desc"/>
        {err && <Err>{err}</Err>}
        <Actions onClose={onClose} busy={busy} testid="tt-req-submit"/>
      </form>
    </SmallModal>
  );
}
